// src/host/services/auth.service.ts
// Enhanced with Cognito authentication methods from WelcomeAuthService

import { GlobalEventService } from '@host/services/event.service';
import { UserLoginEvent, UserLogoutEvent, EventTypes, UserStatusChangedEvent } from 'nexgen-types';
import { useWorkplaceStore } from '@host/stores';
import {
  useAuthStore,
  useOrgStore,
  useSessionStore,
  useUserProfileStore,
  useRecordingStore,
} from '@host/stores';
import { ApiService } from '@shared/services/api.service';
import { Logger } from '../../shared/services/logger.service';
import { initializeSessionAfterLogin } from './session.service';
import { useBreakStore } from '@host/workplace/stores/BreakStore';
import { clearNoiseSuppressionPreferenceCache } from '@shared/utils/noise-suppression-preference.util';
import { clearGuestNoVideoPreference } from '@host/utils/guestVideoPreference';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { AppSDK as MeetingApp } from '@apps/meeting/AppController';

export class AuthService {
  private static instance: AuthService;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // ============================================================================
  // COGNITO AUTHENTICATION METHODS (moved from WelcomeAuthService)
  // ============================================================================

  /**
   * Handle credentials-based login with Cognito
   */
  async loginWithCredentials(email: string, password: string): Promise<any> {
    try {
      const { signIn, checkSession, setRedirect, cognitoLocalSignOut } = useAuthStore.getState();
      await cognitoLocalSignOut();
      const result = await signIn({ username: email, password });

      if (!result) {
        return { success: false, result: null, needsNextStep: true };
      }

      const orgSlug = window.location.pathname.split('/')[1];
      let platformRole: any = null;
      if (result?.isSignedIn) {
        // Ensure orgPid is in session store before any API calls that need x-org-pid header.
        // Without this, _getOrgPid() may return null if welcomeSettings haven't loaded yet.
        try {
          const initData: any = await initializeSessionAfterLogin(orgSlug);
          platformRole = initData.platform_role;
        } catch (sessionErr: any) {
          platformRole = sessionErr.platform_role;
          if (sessionErr.platform_role !== 'normal' && sessionErr.isDeleted) {
            throw sessionErr;
          }
          Logger.warn(
            '[AuthService] Session init failed during login, proceeding with fallback orgPid',
            sessionErr
          );
        }

        if (platformRole !== 'normal') {
          await ApiService.post('/platform/ensure-org-profile', { orgSlug });
        }
      }

      const resp = await ApiService.get<any>(`user-org-preferences/app-settings/profile`);
      if (resp && resp.settings?.lastLoggedIn) {
        if (result?.isSignedIn || result?.nextStep === 'DONE') {
          localStorage.removeItem('meeting-mute-state');
          useWorkplaceStore.getState().setFreshLogin(true);
          await checkSession();
          setRedirect(true);
          return { success: true, result };
        }
      } else {
        await ApiService.put<any>(`user-org-preferences/app-settings/profile`, {
          settings: {
            ...resp.settings,
            lastLoggedIn: new Date().toDateString(),
          },
        });
        return { success: false, showResetPassword: true };
      }

      return { success: false, result, needsNextStep: true };
    } catch (error) {
      console.error('[AuthService] ❌ Credentials login failed:', error);
      throw error;
    }
  }

  /**
   * Handle new password requirement
   */
  async handleNewPassword(challengeResponse: string): Promise<any> {
    try {
      const { confirmSignIn, checkSession } = useAuthStore.getState();
      await confirmSignIn({ challengeResponse });
      await checkSession();
      localStorage.removeItem('meeting-mute-state');
      // Also drop any "No Video" choice from a previous guest session, so a new guest
      // starts from the org's normal policy until they pick again.
      clearGuestNoVideoPreference();
      useWorkplaceStore.getState().setFreshLogin(true);

      return { success: true };
    } catch (error) {
      console.error('[AuthService] ❌ New password failed:', error);
      throw error;
    }
  }

  /**
   * Start SSO login flow
   */
  async startSSOLogin(provider: 'Google' | 'Microsoft' | 'Auth0'): Promise<void> {
    try {
      const { providerSignIn, cognitoLocalSignOut } = useAuthStore.getState();
      await cognitoLocalSignOut();
      useWorkplaceStore.getState().setFreshLogin(true);
      await providerSignIn(provider);
    } catch (error) {
      console.error(`[AuthService] ❌ ${provider} SSO failed:`, error);
      throw error;
    }
  }

  /**
   * Handle guest login
   */
  async handleGuestLogin(): Promise<any> {
    try {
      const { cognitoLocalSignOut } = useAuthStore.getState();
      await cognitoLocalSignOut();
      // useWorkplaceStore.getState().setGuestMode(true); // TODO: Add setGuestMode method to workplace store
      localStorage.removeItem('meeting-mute-state');
      useWorkplaceStore.getState().setFreshLogin(true);

      return { success: true };
    } catch (error) {
      console.error('[AuthService] ❌ Guest login failed:', error);
      throw error;
    }
  }

  /**
   * Logs the user out by ending any active break, cleaning up media streams,
   * signing out of the auth provider, and resetting all application stores.
   *
   * If an error occurs during the primary logout flow, a fallback cleanup
   * sequence is attempted to ensure the session is fully torn down.
   *
   * @returns {Promise<void>} Resolves when logout and cleanup are complete.
   */
  async logout(): Promise<void> {
    // Drop the cached noise-suppression preference so the next user on this browser does not
    // inherit the previous user's setting.
    clearNoiseSuppressionPreferenceCache();

    // Leave any active meeting before logging out — vo.meeting.leave is
    // event-driven (only an explicit leave/room-switch fires it), so
    // without this a logout while in a meeting left the meeting "open" in
    // the activity-metrics pipeline indefinitely. Since active_seconds
    // unions in meeting time unconditionally (see interval-merge.ts), that
    // silently counted the logged-out time as active/in-office instead of
    // a session gap — same class of bug already fixed for idle and break.
    try {
      if (MeetingApp.isInMeeting()) {
        Logger.debug('[AuthService] 📞 User in meeting - leaving before logout...');
        await MeetingApp.service.leaveMeeting();
      }
    } catch (meetingError) {
      console.warn('[AuthService] ⚠️ Failed to leave meeting on logout:', meetingError);
    }

    // End active break before logging out
    const breakStore = useBreakStore.getState();
    if (breakStore.breakDetails) {
      try {
        await ApiService.post('/break/stop', {}, { service: 'app' });
        Logger.debug('[AuthService] ✅ Break ended on logout');
      } catch (breakError) {
        console.warn('[AuthService] ⚠️ Failed to end break on logout:', breakError);
      }
    }

    try {
      // Clean up media streams (camera, mic) and localStorage
      await this.cleanupMediaStreams();

      // Complete authentication logout
      const { signOut } = useAuthStore.getState();
      await signOut();

      // Clear all stores
      useOrgStore.getState().reset();
      useSessionStore.getState().clearSession();
      useUserProfileStore.getState().clear();
      useWorkplaceStore.getState().clearWorkplaceState();
      useAuthStore.getState().clearAuthStore();
      useRecordingStore.getState().clearRecordingSession();
      useBreakStore.getState().reset();
      this.cleanup();
    } catch (err) {
      console.error('[AuthService] ❌ Logout failed:', err);
      // Continue with cleanup even if there was an error
      try {
        await this.cleanupMediaStreams();
        const { signOut } = useAuthStore.getState();
        await signOut();
        useOrgStore.getState().reset();
        useSessionStore.getState().clearSession();
        useUserProfileStore.getState().clear();
        useWorkplaceStore.getState().clearWorkplaceState();
        useAuthStore.getState().clearAuthStore();
        useRecordingStore.getState().clearRecordingSession();
        useBreakStore.getState().reset();
        this.cleanup();
      } catch (error) {
        console.error('[AuthService] ❌ Cleanup after logout failed:', error);
      }
    }
  }

  // ============================================================================
  // EXISTING EVENT HANDLING METHODS (keep unchanged)
  // ============================================================================

  /**
   * Initialize auth event listeners - call this once during app startup
   */
  initialize(): void {
    if (this.isInitialized) {
      Logger.debug('[AuthService] Already initialized');
      return;
    }

    Logger.debug('[AuthService] Initializing auth event handlers');

    GlobalEventService.on({
      event: EventTypes.UserLogin,
      callback: this.handleLocalLogin.bind(this),
    });

    GlobalEventService.on({
      event: EventTypes.UserLoginAck,
      callback: this.handleLoginAck.bind(this),
    });

    GlobalEventService.on({
      event: EventTypes.UserLoginError,
      callback: this.handleLoginError.bind(this),
    });

    // Logout event handlers
    GlobalEventService.on({
      event: EventTypes.UserLogoutAck,
      callback: this.handleLogoutAck.bind(this),
    });

    GlobalEventService.on({
      event: EventTypes.UserLogoutError,
      callback: this.handleLogoutError.bind(this),
    });

    // Optional: Listen for other users' login/logout events
    GlobalEventService.on({
      event: EventTypes.UserLoginTopic,
      callback: this.handleLoginTopic.bind(this),
    });

    GlobalEventService.on({
      event: EventTypes.UserLogoutTopic,
      callback: this.handleLogoutTopic.bind(this),
    });

    this.isInitialized = true;
    Logger.debug('[AuthService] ✅ Auth event handlers registered');
  }

  /**
   * Send login event to server with bootstrap data
   */
  async sendLoginEvent(context: any) {
    try {
      Logger.debug('[AuthService] Sending login event with context:', context);

      const loginEvent = new UserLoginEvent({
        user_pid: context.user_pid,
        room_pid: context.room_pid,
        floor_pid: context.floor_pid,
        org_pid: context.org_pid,
      });

      Logger.debug('[AuthService] Created login event instance:', loginEvent);

      await GlobalEventService.emitTyped(loginEvent);
      Logger.debug('[AuthService] ✅ Login event sent successfully');
    } catch (error) {
      console.error('[AuthService] ❌ Failed to send login event:', error);
      throw error;
    }
  }

  /**
   * Send logout event to server
   */
  async sendLogoutEvent(logoutPayload: {
    user_pid: string;
    floor_pid?: string;
    room_pid?: string;
  }): Promise<void> {
    try {
      const store = useWorkplaceStore.getState();

      if (!logoutPayload.room_pid) {
        logoutPayload.room_pid = store.currentRoom?.pid || store.presence?.room_pid;
      }

      if (!logoutPayload.floor_pid) {
        // currentFloorPresence is set synchronously the moment a live room
        // change happens (OfficeService.navigateToRoom); currentRoom/presence
        // can lag behind it, which was causing logout to broadcast the wrong
        // (often home) floor and leave stale avatars behind in the real room.
        logoutPayload.floor_pid =
          useMultiFloorStateStore.getState().currentFloorPresence ||
          store.currentRoom?.floor_pid ||
          store.presence?.floor_pid ||
          store.homeRoom?.floor_pid;
      }

      Logger.debug('[AuthService] Sending logout event:', logoutPayload);

      try {
        Logger.debug('[AuthService] 🔄 Updating user status to offline in database...');

        await ApiService.request({
          method: 'PATCH',
          endpoint: 'workplace/user/presence',
          data: {
            status: 'offline',
            message: null,
          },
        });

        Logger.debug('[AuthService] ✅ User status updated to offline in database');
      } catch (dbError) {
        console.warn(
          '[AuthService] ⚠️ Failed to update user status in database (will continue with logout):',
          dbError
        );
        // Continue with logout even if DB update fails
      }

      try {
        const userStatusChangeEvent = new UserStatusChangedEvent({
          user_pid: logoutPayload.user_pid,
          floor_pid: logoutPayload.floor_pid,
          room_pid: logoutPayload.room_pid,
          status: 'offline',
          message: 'User logged out',
        });

        await GlobalEventService.emitTyped(userStatusChangeEvent, true);

        Logger.debug('🚀 [LOGOUT-DEBUG] Status changed to offline event sent successfully');
      } catch (statusError) {
        console.warn('🚀 [LOGOUT-DEBUG] Failed to send status change event:', statusError);
      }

      const logoutEvent = new UserLogoutEvent({
        user_pid: logoutPayload.user_pid,
        floor_pid: logoutPayload.floor_pid,
        room_pid: logoutPayload.room_pid,
      });

      Logger.debug(
        logoutPayload.user_pid,
        logoutPayload.floor_pid,
        logoutPayload.room_pid,
        'helloooooo'
      );
      await GlobalEventService.emitTyped(logoutEvent, true);
      Logger.debug('[AuthService] ✅ Logout event sent successfully');
    } catch (error) {
      console.error('[AuthService] ❌ Failed to send logout event:', error);
    }
  }

  // === Login Event Handlers === (keep unchanged)

  private async handleLocalLogin(event: any): Promise<void> {
    Logger.debug('[AuthService] ✅ Login event handled:', event);
    Logger.debug('[AuthService] Login event has org_id:', event.org_pid);

    // CRITICAL: Reinitialize socket connection with user context after login
    try {
      const { GlobalSocketService } = await import('@host/services/socket.service');
      await GlobalSocketService.reinitializeAfterLogin();
      Logger.debug('[AuthService] ✅ Socket connection reinitialized after login');
    } catch (_error) {
      console.error(
        '[AuthService] ❌ Failed to reinitialize socket connection after login:',
        _error
      );
    }
  }

  private handleLoginAck(event: any): void {
    Logger.debug('[AuthService] ✅ Login ACK received:', event);

    if (event?.subscribed_topics) {
      Logger.debug('[AuthService] 📡 Auto-subscribed to topics:', event.subscribed_topics);
    }

    Logger.debug('[AuthService] 🎉 Successfully connected to real-time events');
  }

  private handleLoginError(event: any): void {
    console.error('[AuthService] ❌ Login ERROR received:', event);

    const errorMessage = event?.error || event?.message || 'Login event failed';
    console.error('[AuthService] Login error details:', errorMessage);
  }

  // === Logout Event Handlers === (keep unchanged)

  private handleLogoutAck(event: any): void {
    Logger.debug('[AuthService] ✅ Logout ACK received:', event);
    Logger.debug('[AuthService] Successfully disconnected from real-time events');
  }

  private handleLogoutError(event: any): void {
    console.error('[AuthService] ❌ Logout ERROR received:', event);
  }

  // === Topic Event Handlers === (keep unchanged)

  private handleLoginTopic(event: any): void {
    Logger.debug('[AuthService] 📡 Another user logged in:', event);
  }

  private handleLogoutTopic(event: any): void {
    Logger.debug('[AuthService] 📡 Another user logged out:', event);
  }

  // === Utility Methods === (keep unchanged)

  getAuthStatus(): {
    isInitialized: boolean;
    timestamp: string;
  } {
    return {
      isInitialized: this.isInitialized,
      timestamp: new Date().toISOString(),
    };
  }

  async handleLogout(logoutPayload: {
    user_pid: string;
    floor_pid?: string;
    room_pid?: string;
  }): Promise<void> {
    try {
      await this.sendLogoutEvent(logoutPayload);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      Logger.debug('[AuthService] Logout flow completed');
    } catch (error) {
      console.error('[AuthService] Error during logout flow:', error);
    }
  }

  cleanup(): void {
    if (!this.isInitialized) return;

    Logger.debug('[AuthService] Cleaning up auth event handlers');

    GlobalEventService.off(EventTypes.UserLogin, this.handleLocalLogin.bind(this));
    GlobalEventService.off(EventTypes.UserLoginAck, this.handleLoginAck.bind(this));
    GlobalEventService.off(EventTypes.UserLoginError, this.handleLoginError.bind(this));
    GlobalEventService.off(EventTypes.UserLogoutAck, this.handleLogoutAck.bind(this));
    GlobalEventService.off(EventTypes.UserLogoutError, this.handleLogoutError.bind(this));
    GlobalEventService.off(EventTypes.UserLoginTopic, this.handleLoginTopic.bind(this));
    GlobalEventService.off(EventTypes.UserLogoutTopic, this.handleLogoutTopic.bind(this));

    this.isInitialized = false;
    Logger.debug('[AuthService] ✅ Auth cleanup completed');
  }

  /**
   * Cleanup only media streams and media-related localStorage
   */
  private async cleanupMediaStreams(): Promise<void> {
    try {
      // Stop ALL navigator media streams (camera, mic)
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            if (stream) {
              stream.getTracks().forEach((track) => {
                track.stop();
              });
            }
          } catch (error) {
            console.warn('[AuthService] ❌ No active media stream to stop:', error);
            // No active stream to stop
          }
          // Also stop any existing streams from DOM
          const videoElements = document.querySelectorAll('video');
          videoElements.forEach((video) => {
            if (video.srcObject && video.srcObject instanceof MediaStream) {
              video.srcObject.getTracks().forEach((track) => track.stop());
              video.srcObject = null;
            }
          });
          const audioElements = document.querySelectorAll('audio');
          audioElements.forEach((audio) => {
            if (audio.srcObject && audio.srcObject instanceof MediaStream) {
              audio.srcObject.getTracks().forEach((track) => track.stop());
              audio.srcObject = null;
            }
          });
        }
      } catch (err) {
        console.error('[AuthService] ❌ Failed to stop media streams:', err);
      }
      // Clear any media-related localStorage
      try {
        const mediaKeys = [
          'preferredVideoDeviceId',
          'preferredAudioDeviceId',
          'currentMeetingId',
          'webextoken',
          'jitsi-settings',
          'jitsi-local-storage',
          'jitsiMeetId',
          'callStatsUserName',
        ];
        mediaKeys.forEach((key) => {
          localStorage.removeItem(key);
        });
      } catch (err) {
        console.error('[AuthService] ❌ Failed to clear media-related localStorage:', err);
      }
    } catch (error) {
      console.error('[AuthService] ❌ Cleanup media streams failed:', error);
      // Don't throw - we want logout to continue even if media cleanup fails
    }
  }
}

// Export singleton
export const authService = AuthService.getInstance();
